import {
  PracticeTimer,
  TIMER_STATES,
  formatTimerTime,
} from './practice-timer.js';
import { renderTimerDisplay, timerDisplayTemplate } from './timer-display.js';
import {
  addDualTimerHistoryRecord,
  addTimerHistoryRecord,
  clearTimerHistory,
  createTimerHistoryExport,
  formatTimerHistoryExportFilename,
  formatHistoryDate,
  importTimerHistory,
  loadTimerHistory,
} from './timer-history.js';
import {
  loadTimerSaveHistoryPreference,
  saveTimerSaveHistoryPreference,
} from './timer-preferences.js';

const STATE_COPY = Object.freeze({
  [TIMER_STATES.IDLE]: ['准备就绪', '按 Space 或“开始”启动'],
  [TIMER_STATES.RUNNING]: ['计时中', '再次按 Space 停止'],
  [TIMER_STATES.PAUSED]: ['已暂停', '点击“开始”继续，或停止保留成绩'],
  [TIMER_STATES.STOPPED]: ['成绩已锁定', '点击“重置”后开始下一次练习'],
});

const DUAL_STATE_COPY = Object.freeze({
  [TIMER_STATES.IDLE]: ['准备就绪', '按键或点击“开始”启动'],
  [TIMER_STATES.RUNNING]: ['计时中', '再次按键或点击“停止”完成'],
  [TIMER_STATES.PAUSED]: ['已暂停', '点击“开始”继续，或点击“停止”完成'],
  [TIMER_STATES.STOPPED]: ['已完成', '成绩已锁定，重置后可重新计时'],
});

const DIRECTION_KEY_CODES = Object.freeze(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);
const RESULT_FLASH_FALLBACK_MS = 1800;

export class DirectionKeyGate {
  constructor() {
    this.pressedKeys = new Set();
  }

  press(code) {
    if (!DIRECTION_KEY_CODES.includes(code)) return false;
    const shouldTrigger = this.pressedKeys.size === 0;
    this.pressedKeys.add(code);
    return shouldTrigger;
  }

  release(code) {
    this.pressedKeys.delete(code);
  }

  reset() {
    this.pressedKeys.clear();
  }
}

export function resetDualTimers(timers) {
  timers.forEach((timer) => timer.reset());
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function playerName(value, fallback) {
  return String(value ?? '').trim() || fallback;
}

function historyRecordTemplate(record) {
  if (record.mode === 'dual') {
    const player1 = playerName(record.player1, '玩家1');
    const player2 = playerName(record.player2, '玩家2');
    return `<button class="timer-history-record is-dual" type="button" data-history-id="${escapeHtml(record.id)}" aria-label="查看第 ${record.serial} 条双人历史记录，${escapeHtml(player1)} 成绩 ${formatTimerTime(record.time1)}，${escapeHtml(player2)} 成绩 ${formatTimerTime(record.time2)}">
      <span class="history-serial" data-label="序号">#${record.serial}</span>
      <strong data-label="双方用时">${formatTimerTime(record.time1)} / ${formatTimerTime(record.time2)}</strong>
      <span data-label="模式与阶数">双人 · ${record.order}×${record.order}</span>
      <time data-label="日期时间" datetime="${record.recordedAt}">${formatHistoryDate(record.recordedAt)}</time>
      <span class="history-username" data-label="玩家">${escapeHtml(player1)} / ${escapeHtml(player2)}</span>
    </button>`;
  }

  return `<button class="timer-history-record" type="button" data-history-id="${escapeHtml(record.id)}" aria-label="查看第 ${record.serial} 条历史记录，成绩 ${formatTimerTime(record.elapsedMs)}">
    <span class="history-serial" data-label="序号">#${record.serial}</span>
    <strong data-label="用时">${formatTimerTime(record.elapsedMs)}</strong>
    <span data-label="阶数">${record.order}×${record.order}</span>
    <time data-label="日期时间" datetime="${record.recordedAt}">${formatHistoryDate(record.recordedAt)}</time>
    <span class="history-username" data-label="用户名">${escapeHtml(record.username)}</span>
  </button>`;
}

function orderOptions(selectedOrder) {
  return [2, 3, 4, 5].map((order) => (
    `<option value="${order}"${String(order) === String(selectedOrder) ? ' selected' : ''}>${order}×${order}</option>`
  )).join('');
}

function timerSettingsTemplate(mode, settings) {
  const names = mode === 'dual'
    ? `<label><span>玩家1</span><input type="text" maxlength="80" autocomplete="off" data-timer-player-name="1" value="${escapeHtml(settings.player1)}" placeholder="玩家1"></label>
       <label><span>玩家2</span><input type="text" maxlength="80" autocomplete="off" data-timer-player-name="2" value="${escapeHtml(settings.player2)}" placeholder="玩家2"></label>`
    : `<label><span>用户名</span><input type="text" maxlength="80" autocomplete="off" data-timer-username value="${escapeHtml(settings.singleUsername)}" placeholder="可留空"></label>`;

  return `<div class="timer-session-settings${mode === 'dual' ? ' is-dual' : ''}" aria-label="本次${mode === 'dual' ? '比赛' : '练习'}设置">
    ${names}
    <label>
      <span>阶数</span>
      <select data-timer-order aria-label="选择本次${mode === 'dual' ? '比赛' : '练习'}阶数">${orderOptions(settings.order)}</select>
    </label>
    <label class="timer-save-option">
      <input type="checkbox" data-timer-save-history${settings.saveHistory ? ' checked' : ''}>
      <span><strong>保存至历史记录</strong><small>${mode === 'dual' ? '双方完成后保存比赛成绩' : '停止后自动保存本次成绩'}</small></span>
    </label>
    <button class="timer-scramble-launch" type="button" data-action="open-scramble">生成打乱公式 <span aria-hidden="true">→</span></button>
  </div>`;
}

function timerControlsTemplate(player = null) {
  const playerAttribute = player === null ? '' : ` data-timer-player="${player}"`;
  return `<div class="timer-controls" aria-label="${player === null ? '' : `玩家${player}`}计时控制">
    <button class="primary-button" type="button" data-timer-action="start"${playerAttribute}>开始</button>
    <button class="secondary-button" type="button" data-timer-action="stop"${playerAttribute} disabled>停止</button>
    <button class="secondary-button" type="button" data-timer-action="pause"${playerAttribute} disabled>暂停</button>
    <button class="secondary-button" type="button" data-timer-action="reset"${playerAttribute}>重置</button>
  </div>`;
}

function singleTimerTemplate() {
  return `${timerDisplayTemplate({ className: 'single-timer-display' })}
    <div class="timer-state-row" aria-live="polite">
      <strong data-timer-status>准备就绪</strong>
      <span data-timer-hint>按 Space 或“开始”启动</span>
    </div>
    ${timerControlsTemplate()}
    <p class="timer-keyboard-hint"><kbd>Space</kbd><span>开始 / 停止</span></p>`;
}

function dualPlayerTemplate(player, keyLabel) {
  return `<article class="dual-timer-panel" data-dual-player="${player}">
    <div class="dual-player-heading"><span>玩家${player}</span><strong data-dual-player-label="${player}">玩家${player}</strong></div>
    ${timerDisplayTemplate({ className: 'dual-timer-display' })}
    <div class="timer-state-row" aria-live="polite">
      <strong data-timer-status>准备就绪</strong>
      <span data-timer-hint>按键或点击“开始”启动</span>
    </div>
    ${timerControlsTemplate(player)}
    <p class="timer-keyboard-hint"><kbd>${keyLabel}</kbd><span>开始 / 停止</span></p>
  </article>`;
}

function dualTimerTemplate() {
  return `<div class="dual-timer-grid">
    ${dualPlayerTemplate(1, 'Left Ctrl')}
    ${dualPlayerTemplate(2, '方向键')}
  </div>
  <dialog class="winner-dialog" data-dual-winner-dialog aria-labelledby="dual-winner-title">
    <p class="winner-dialog-label" id="dual-winner-title">获胜者：</p>
    <strong class="winner-dialog-name" data-dual-winner-name></strong>
    <p class="winner-dialog-score-label">成绩：</p>
    <span class="winner-dialog-score" data-dual-winner-time></span>
  </dialog>`;
}

function historySectionTemplate() {
  return `<section class="timer-history-section" data-timer-history aria-labelledby="timer-history-title">
    <div class="timer-history-heading">
      <div><p class="section-kicker">本机数据</p><h2 id="timer-history-title">历史记录</h2></div>
      <div class="timer-history-actions">
        <button class="secondary-button" type="button" data-history-action="export">导出历史记录</button>
        <button class="secondary-button" type="button" data-history-action="import">导入历史记录</button>
        <input type="file" accept="application/json,.json" data-history-import-input hidden>
        <button class="danger-button history-clear-button" type="button" data-history-action="request-clear" disabled>清空全部历史记录</button>
      </div>
    </div>
    <div class="timer-history-table">
      <div class="timer-history-columns" aria-hidden="true">
        <span>序号</span><span>用时 / 双方用时</span><span>阶数 / 模式</span><span>日期时间</span><span>用户名 / 玩家</span>
      </div>
      <div data-history-list></div>
    </div>
    <p class="timer-history-empty" data-history-empty>还没有保存的成绩。勾选“保存至历史记录”后，完成计时即可添加。</p>
  </section>

  <dialog class="confirm-dialog danger-confirm" data-history-clear-dialog aria-labelledby="history-clear-title">
    <h2 id="history-clear-title">确认清空全部历史记录？</h2>
    <p>所有已保存的计时成绩都将从此浏览器删除，此操作无法撤销。</p>
    <div class="confirm-actions">
      <button class="secondary-button" type="button" data-history-action="cancel-clear">取消</button>
      <button class="danger-button" type="button" data-history-action="confirm-clear">确认清空</button>
    </div>
  </dialog>`;
}

export function timerPageTemplate({
  mode = 'single',
  settings = {
    singleUsername: '',
    player1: '',
    player2: '',
    order: '3',
    saveHistory: true,
  },
} = {}) {
  const isDual = mode === 'dual';
  return `
    <section class="timer-page${isDual ? ' is-dual-mode' : ''}" aria-labelledby="timer-title">
      <div class="timer-heading">
        <button class="back-button" type="button" data-action="home"><span aria-hidden="true">←</span> 返回</button>
        <div class="timer-heading-copy">
          <p class="eyebrow">练习工具</p>
          <h1 id="timer-title">${isDual ? '双人计时器' : '练习计时器'}</h1>
          <p>${isDual ? '同阶竞技，双方计时与控制完全独立。' : '面向手动复原练习的高精度正计时。'}</p>
        </div>
        <button class="secondary-button timer-mode-toggle" type="button" data-timer-mode-toggle>${isDual ? '单人模式' : '双人模式'}</button>
      </div>

      <div class="timer-workspace" data-timer-mode="${mode}">
        ${timerSettingsTemplate(mode, settings)}
        ${isDual ? dualTimerTemplate() : singleTimerTemplate()}
      </div>

      ${historySectionTemplate()}
    </section>
  `;
}

export class PracticeTimerPage {
  /**
   * @param {any} root
   * @param {{ announce?: (message: string) => void, storage?: any }} [options]
   */
  constructor(root, options = {}) {
    const { announce = (_message) => {}, storage } = options;
    this.root = root;
    this.announce = announce;
    this.storage = storage;
    this.mode = 'single';
    this.settings = {
      singleUsername: '',
      player1: '',
      player2: '',
      order: '3',
      saveHistory: loadTimerSaveHistoryPreference(this.storage),
    };
    this.timers = [];
    this.lastStopSaved = false;
    this.historySaveError = '';
    this.dualResultResolved = false;
    this.dualWinner = null;
    this.winnerTimeout = null;
    this.winnerRevealTimeout = null;
    this.winnerAnimationTarget = null;
    this.handleWinnerAnimationEnd = null;
    this.directionKeyGate = new DirectionKeyGate();
    this.handleClick = (event) => this.#handleClick(event);
    this.handleInput = (event) => this.#handleInput(event);
    this.handleChange = (event) => this.#handleChange(event);
    this.handleKeydown = (event) => this.#handleKeydown(event);
    this.handleKeyup = (event) => this.#handleKeyup(event);
    this.root.addEventListener('click', this.handleClick);
    this.root.addEventListener('input', this.handleInput);
    this.root.addEventListener('change', this.handleChange);
    window.addEventListener('keydown', this.handleKeydown, { capture: true });
    window.addEventListener('keyup', this.handleKeyup, { capture: true });
    this.#mountMode();
  }

  dispose() {
    this.#disposeTimers();
    this.#clearWinnerTimeout();
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('input', this.handleInput);
    this.root.removeEventListener('change', this.handleChange);
    window.removeEventListener('keydown', this.handleKeydown, { capture: true });
    window.removeEventListener('keyup', this.handleKeyup, { capture: true });
  }

  #mountMode() {
    this.root.innerHTML = timerPageTemplate({ mode: this.mode, settings: this.settings });
    this.dualResultResolved = false;
    this.dualWinner = null;
    this.lastStopSaved = false;
    this.historySaveError = '';

    if (this.mode === 'dual') {
      this.timers = [0, 1].map((index) => new PracticeTimer({
        onTick: (elapsedMs) => this.#renderDualTime(index, elapsedMs),
        onStateChange: (state, reason) => this.#renderDualState(index, state, reason),
      }));
      this.timers.forEach((_timer, index) => {
        this.#renderDualTime(index, 0);
        this.#renderDualState(index, TIMER_STATES.IDLE, 'mount');
      });
      this.#syncDualPlayerLabels();
    } else {
      this.timer = new PracticeTimer({
        onTick: (elapsedMs) => this.#renderSingleTime(elapsedMs),
        onStateChange: (state, reason) => this.#renderSingleState(state, reason),
      });
      this.timers = [this.timer];
      this.#renderSingleTime(0);
      this.#renderSingleState(TIMER_STATES.IDLE, 'mount');
    }
    this.#renderHistory();
  }

  #disposeTimers() {
    this.timers.forEach((timer) => timer.dispose());
    this.timers = [];
    this.timer = null;
  }

  #snapshotSettings() {
    this.settings.order = this.root.querySelector('[data-timer-order]')?.value ?? this.settings.order;
    this.settings.saveHistory = Boolean(this.root.querySelector('[data-timer-save-history]')?.checked);
    if (this.mode === 'dual') {
      this.settings.player1 = this.root.querySelector('[data-timer-player-name="1"]')?.value ?? this.settings.player1;
      this.settings.player2 = this.root.querySelector('[data-timer-player-name="2"]')?.value ?? this.settings.player2;
    } else {
      this.settings.singleUsername = this.root.querySelector('[data-timer-username]')?.value ?? this.settings.singleUsername;
    }
  }

  #switchMode() {
    this.#snapshotSettings();
    this.#disposeTimers();
    this.#clearDualResult();
    this.directionKeyGate.reset();
    this.mode = this.mode === 'single' ? 'dual' : 'single';
    this.#mountMode();
    this.announce(`已切换至${this.mode === 'dual' ? '双人' : '单人'}模式`);
  }

  #handleClick(event) {
    if (event.target.closest('[data-timer-mode-toggle]')) {
      this.#switchMode();
      return;
    }

    const button = event.target.closest('[data-timer-action]');
    if (button) {
      if (this.mode === 'dual') this.#runDualAction(Number(button.dataset.timerPlayer) - 1, button.dataset.timerAction);
      else this.#runSingleAction(button.dataset.timerAction);
      return;
    }

    const historyAction = event.target.closest('[data-history-action]')?.dataset.historyAction;
    if (historyAction === 'request-clear') this.root.querySelector('[data-history-clear-dialog]')?.showModal();
    else if (historyAction === 'cancel-clear') this.root.querySelector('[data-history-clear-dialog]')?.close();
    else if (historyAction === 'export') this.#exportHistory();
    else if (historyAction === 'import') this.root.querySelector('[data-history-import-input]')?.click();
    else if (historyAction === 'confirm-clear') {
      clearTimerHistory(this.storage);
      this.root.querySelector('[data-history-clear-dialog]')?.close();
      this.#renderHistory();
      this.announce('全部计时历史记录已清空');
    } else {
      const recordButton = event.target.closest('[data-history-id]');
      if (recordButton) window.location.hash = `#/timer/history/${encodeURIComponent(recordButton.dataset.historyId)}`;
    }
  }

  #handleInput(event) {
    if (this.mode === 'dual' && event.target.matches('[data-timer-player-name]')) this.#syncDualPlayerLabels();
  }

  #handleChange(event) {
    if (event.target.matches('[data-timer-save-history]')) {
      this.settings.saveHistory = event.target.checked;
      try {
        saveTimerSaveHistoryPreference(event.target.checked, this.storage);
      } catch (cause) {
        this.announce(`历史记录保存设置写入失败：${cause?.message ?? String(cause)}`);
      }
      return;
    }
    if (event.target.matches('[data-history-import-input]')) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (file) this.#importHistoryFile(file);
    }
  }

  #exportHistory() {
    try {
      const exportedAt = new Date();
      const payload = createTimerHistoryExport(this.storage, exportedAt);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = formatTimerHistoryExportFilename(exportedAt);
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.announce(`已导出 ${payload.records.length} 条历史记录`);
    } catch (cause) {
      this.announce(`历史记录导出失败：${cause?.message ?? String(cause)}`);
    }
  }

  async #importHistoryFile(file) {
    try {
      const payload = JSON.parse(await file.text());
      const result = importTimerHistory(payload, { storage: this.storage });
      this.#renderHistory();
      const skipped = result.skippedCount ? `，跳过 ${result.skippedCount} 条无效记录` : '';
      this.announce(`成功导入 ${result.importedCount} 条历史记录${skipped}`);
    } catch (cause) {
      this.announce(`历史记录导入失败：${cause?.message ?? String(cause)}`);
    }
  }

  #handleKeydown(event) {
    if (this.mode === 'single') {
      if (event.code !== 'Space') return;
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      if (this.timer.state === TIMER_STATES.RUNNING) this.#runSingleAction('stop');
      else if (this.timer.state === TIMER_STATES.IDLE || this.timer.state === TIMER_STATES.PAUSED) this.#runSingleAction('start');
      else this.announce('请先重置计时器，再开始下一次练习');
      return;
    }

    const isDirectionKey = DIRECTION_KEY_CODES.includes(event.code);
    const playerIndex = event.code === 'ControlLeft' ? 0 : isDirectionKey ? 1 : -1;
    if (playerIndex < 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (isDirectionKey) {
      if (event.repeat) {
        this.directionKeyGate.press(event.code);
        return;
      }
      if (!this.directionKeyGate.press(event.code)) return;
    } else if (event.repeat) return;
    const timer = this.timers[playerIndex];
    if (timer.state === TIMER_STATES.RUNNING) this.#runDualAction(playerIndex, 'stop');
    else if (timer.state === TIMER_STATES.IDLE || timer.state === TIMER_STATES.PAUSED) this.#runDualAction(playerIndex, 'start');
    else this.announce(`玩家${playerIndex + 1}请先重置计时器`);
  }

  #handleKeyup(event) {
    if (!DIRECTION_KEY_CODES.includes(event.code)) return;
    this.directionKeyGate.release(event.code);
    if (this.mode !== 'dual') return;
    event.preventDefault();
    event.stopPropagation();
  }

  #runSingleAction(action) {
    if (action === 'start') {
      if (this.timer.start()) this.announce(this.timer.elapsedMs > 0 ? '计时已继续' : '计时已开始');
    } else if (action === 'stop') {
      if (this.timer.stop()) {
        const suffix = this.lastStopSaved
          ? '，已保存至历史记录'
          : this.historySaveError ? `，但保存失败：${this.historySaveError}` : '';
        this.announce(`计时停止，成绩 ${formatTimerTime(this.timer.getElapsedMs())}${suffix}`);
      }
    } else if (action === 'pause') {
      if (this.timer.pause()) this.announce('计时已暂停');
    } else if (action === 'reset') {
      this.timer.reset();
      this.announce('计时器已重置');
    }
  }

  #runDualAction(index, action) {
    const timer = this.timers[index];
    if (!timer) return;
    if (action === 'start') {
      if (timer.start()) this.announce(`玩家${index + 1}计时${timer.elapsedMs > 0 ? '已继续' : '已开始'}`);
    } else if (action === 'stop') {
      if (timer.stop()) this.announce(`玩家${index + 1}完成，成绩 ${formatTimerTime(timer.getElapsedMs())}`);
    } else if (action === 'pause') {
      if (timer.pause()) this.announce(`玩家${index + 1}计时已暂停`);
    } else if (action === 'reset') {
      this.#clearDualResult();
      resetDualTimers(this.timers);
      this.announce('双人计时器已全部重置');
    }
  }

  #renderSingleTime(elapsedMs) {
    renderTimerDisplay(this.root.querySelector('.single-timer-display'), elapsedMs);
  }

  #renderSingleState(state, reason) {
    const [status, hint] = STATE_COPY[state];
    const display = this.root.querySelector('.single-timer-display');
    this.root.querySelector('[data-timer-status]').textContent = status;
    this.root.querySelector('[data-timer-hint]').textContent = hint;
    this.#applyDisplayState(display, state);
    if (state === TIMER_STATES.STOPPED) this.#saveStoppedTime();
    else if (state === TIMER_STATES.IDLE) {
      this.lastStopSaved = false;
      this.historySaveError = '';
    }

    this.#syncControlState(this.root, state);
    if (reason === 'maximum') {
      this.announce(`计时器已达到最大显示 99:99.999${this.lastStopSaved ? '，成绩已保存' : ''}`);
    }
  }

  #renderDualTime(index, elapsedMs) {
    renderTimerDisplay(this.#dualPanel(index)?.querySelector('[data-timer-display]'), elapsedMs);
  }

  #renderDualState(index, state, reason) {
    const panel = this.#dualPanel(index);
    if (!panel) return;
    const [status, hint] = DUAL_STATE_COPY[state];
    panel.querySelector('[data-timer-status]').textContent = status;
    panel.querySelector('[data-timer-hint]').textContent = hint;
    this.#applyDisplayState(panel.querySelector('[data-timer-display]'), state);
    this.#syncControlState(panel, state);

    if (state === TIMER_STATES.STOPPED) this.#resolveDualResult(index);
    if (reason === 'maximum') this.announce(`玩家${index + 1}计时器已达到最大显示 99:99.999`);
  }

  #applyDisplayState(display, state) {
    if (!display) return;
    display.dataset.timerState = state;
    display.classList.toggle('is-running', state === TIMER_STATES.RUNNING);
    display.classList.toggle('is-paused', state === TIMER_STATES.PAUSED);
    display.classList.remove('is-flashing');
    if (state === TIMER_STATES.STOPPED) display.classList.add('is-flashing');
  }

  #syncControlState(scope, state) {
    const startButton = scope.querySelector('[data-timer-action="start"]');
    const stopButton = scope.querySelector('[data-timer-action="stop"]');
    const pauseButton = scope.querySelector('[data-timer-action="pause"]');
    startButton.disabled = state !== TIMER_STATES.IDLE && state !== TIMER_STATES.PAUSED;
    startButton.textContent = state === TIMER_STATES.PAUSED ? '继续' : '开始';
    stopButton.disabled = state !== TIMER_STATES.RUNNING && state !== TIMER_STATES.PAUSED;
    pauseButton.disabled = state !== TIMER_STATES.RUNNING;
  }

  #dualPanel(index) {
    return this.root.querySelector(`[data-dual-player="${index + 1}"]`);
  }

  #syncDualPlayerLabels() {
    [1, 2].forEach((player) => {
      const value = this.root.querySelector(`[data-timer-player-name="${player}"]`)?.value;
      const label = this.root.querySelector(`[data-dual-player-label="${player}"]`);
      if (label) label.textContent = playerName(value, `玩家${player}`);
    });
  }

  #saveStoppedTime() {
    this.lastStopSaved = false;
    this.historySaveError = '';
    if (!this.root.querySelector('[data-timer-save-history]')?.checked) return;
    try {
      addTimerHistoryRecord({
        elapsedMs: this.timer.getElapsedMs(),
        order: Number(this.root.querySelector('[data-timer-order]')?.value),
        recordedAt: new Date(),
        username: this.root.querySelector('[data-timer-username]')?.value ?? '',
      }, { storage: this.storage });
      this.lastStopSaved = true;
      this.#renderHistory();
    } catch (cause) {
      this.historySaveError = cause?.message ?? String(cause);
    }
  }

  #resolveDualResult(lastPlayerIndex) {
    if (this.dualResultResolved || this.timers.some((timer) => timer.state !== TIMER_STATES.STOPPED)) return;
    const [time1, time2] = this.timers.map((timer) => timer.getElapsedMs());
    this.dualWinner = time1 === time2 ? 'tie' : time1 < time2 ? 'player1' : 'player2';
    this.dualResultResolved = true;
    this.#syncDualPlayerLabels();

    let saveSuffix = '';
    if (this.root.querySelector('[data-timer-save-history]')?.checked) {
      try {
        addDualTimerHistoryRecord({
          player1: this.root.querySelector('[data-timer-player-name="1"]')?.value ?? '',
          player2: this.root.querySelector('[data-timer-player-name="2"]')?.value ?? '',
          time1,
          time2,
          order: Number(this.root.querySelector('[data-timer-order]')?.value),
          recordedAt: new Date(),
        }, { storage: this.storage });
        saveSuffix = '，比赛记录已保存';
        this.#renderHistory();
      } catch (cause) {
        saveSuffix = `，但保存失败：${cause?.message ?? String(cause)}`;
      }
    }

    const winnerText = this.dualWinner === 'tie'
      ? '平局'
      : playerName(
        this.root.querySelector(`[data-timer-player-name="${this.dualWinner === 'player1' ? 1 : 2}"]`)?.value,
        this.dualWinner === 'player1' ? '玩家1' : '玩家2',
      );
    const winnerTime = Math.min(time1, time2);
    this.#scheduleWinnerDialog(lastPlayerIndex, winnerText, winnerTime, saveSuffix);
  }

  #scheduleWinnerDialog(lastPlayerIndex, winnerText, winnerTime, saveSuffix) {
    const display = this.#dualPanel(lastPlayerIndex)?.querySelector('[data-timer-display]');
    const animatedSegment = display?.querySelector('.segment.is-lit');
    const showDialog = () => {
      if (!this.dualResultResolved) return;
      this.#clearWinnerRevealWait();
      this.#showWinnerDialog(winnerText, winnerTime, saveSuffix);
    };

    if (animatedSegment) {
      this.winnerAnimationTarget = animatedSegment;
      this.handleWinnerAnimationEnd = showDialog;
      animatedSegment.addEventListener('animationend', this.handleWinnerAnimationEnd, { once: true });
    }
    this.winnerRevealTimeout = window.setTimeout(showDialog, RESULT_FLASH_FALLBACK_MS);
  }

  #showWinnerDialog(winnerText, winnerTime, saveSuffix) {
    const dialog = this.root.querySelector('[data-dual-winner-dialog]');
    dialog.querySelector('[data-dual-winner-name]').textContent = winnerText;
    dialog.querySelector('[data-dual-winner-time]').textContent = formatTimerTime(winnerTime);
    dialog.showModal();
    dialog.addEventListener('close', () => {
      if (this.dualResultResolved) this.#applyWinnerHighlight();
    }, { once: true });
    this.winnerTimeout = window.setTimeout(() => {
      this.winnerTimeout = null;
      if (dialog.open) dialog.close();
      this.#applyWinnerHighlight();
    }, 2000);
    this.announce(`${this.dualWinner === 'tie' ? '双方成绩相同' : `获胜者：${winnerText}`}${saveSuffix}`);
  }

  #applyWinnerHighlight() {
    this.root.querySelectorAll('[data-dual-player]').forEach((panel) => panel.classList.remove('is-winner'));
    if (this.dualWinner === 'player1') this.#dualPanel(0)?.classList.add('is-winner');
    else if (this.dualWinner === 'player2') this.#dualPanel(1)?.classList.add('is-winner');
  }

  #clearWinnerTimeout() {
    if (this.winnerTimeout !== null) window.clearTimeout(this.winnerTimeout);
    this.winnerTimeout = null;
    this.#clearWinnerRevealWait();
  }

  #clearWinnerRevealWait() {
    if (this.winnerRevealTimeout !== null) window.clearTimeout(this.winnerRevealTimeout);
    this.winnerRevealTimeout = null;
    if (this.winnerAnimationTarget && this.handleWinnerAnimationEnd) {
      this.winnerAnimationTarget.removeEventListener('animationend', this.handleWinnerAnimationEnd);
    }
    this.winnerAnimationTarget = null;
    this.handleWinnerAnimationEnd = null;
  }

  #clearDualResult() {
    this.#clearWinnerTimeout();
    this.dualResultResolved = false;
    this.dualWinner = null;
    const dialog = this.root.querySelector('[data-dual-winner-dialog]');
    if (dialog) {
      if (dialog.open) dialog.close();
      const winnerName = dialog.querySelector('[data-dual-winner-name]');
      const winnerTime = dialog.querySelector('[data-dual-winner-time]');
      if (winnerName) winnerName.textContent = '';
      if (winnerTime) winnerTime.textContent = '';
    }
    this.root.querySelectorAll('[data-dual-player]').forEach((panel) => panel.classList.remove('is-winner'));
  }

  #renderHistory() {
    const records = loadTimerHistory(this.storage);
    const list = this.root.querySelector('[data-history-list]');
    const empty = this.root.querySelector('[data-history-empty]');
    const clearButton = this.root.querySelector('[data-history-action="request-clear"]');
    if (list) list.innerHTML = records.map(historyRecordTemplate).join('');
    if (empty) empty.hidden = records.length > 0;
    if (clearButton) clearButton.disabled = records.length === 0;
  }

  scrollToHistory() {
    this.root.querySelector('[data-timer-history]')?.scrollIntoView({ block: 'start' });
  }
}
